import React, { useEffect, useState } from "react";
import { applySortFilter } from "../../../utils/tableFunctions";
import { showErrorMsg } from "../../../utils/toast";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import ListHead from "../../../components/table/ListHead";
import Scrollbar from "../../../components/table/Scrollbar";
import SearchNotFound from "../../../components/table/SearchNotFound";
import {
  Log,
  sendDataWithPost,
  getExchangeRateFilterConditions,
} from "../../../services/services";
import EditDialog from "./EditDialog";
import { StyledTableRow } from "../../../components/TableRow";
import FormSearch from "../../../components/FormSearch";
import CircularLoader from "../../../components/circularLoader";
import { pageNumber, rowsPerPage, HARDCODED_FROM_CURRENCY, EXCHANGE_RATE_YEAR_THRESHOLD} from "../../../utils/constants";
import Pagination from "../../../components/Pagination";
import moment from "moment";
import { styles } from "../../../utils/styles";

const TABLE_HEAD = [
  { id: "from_currency", label: "From Currency" },
  { id: "to_currency", label: "To Currency" },
  { id: "rate_date", label: "Rate Date" },
  { id: "rate", label: "Rate" },
  { id: "edit" },
];

const ExchangeRates = ({ userRole }) => {
  const exchangeRateRole = userRole || [];
  
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("rate_date");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState({});

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [exchangeRateFilter, setExchangeRateFilter] = useState({});
  const [selectedId, setSelectedId] = useState();
  const [fLoaded, setFLoaded] = useState(false);
  const initialDefaultValue = {
    rate_date: moment().subtract(1, 'month').format("YYYY-MM"),
  };
  const [defaultValue, setDefaultValue] = useState(initialDefaultValue);
  const [formKey, setFormKey] = useState(0);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/payout/exchange_rate/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    });
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const renderData = (res) => {
    setTableData(res);
  };

  useEffect(() => {
    const initialFilter = {
      rate_date: moment().subtract(1, 'month').format("YYYY-MM"),
    };
    setFilter(initialFilter);
    queryPage(pagination, initialFilter);
    getExchangeRateFilterConditions(setExchangeRateFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenCreateDialog(false);
    setSelectedId(null);
  };

  const onFilterSubmit = (fParams) => {
    if (fParams.rate_date) {
      const dateMoment = moment(fParams.rate_date);
      if (!dateMoment.isValid()) {
        showErrorMsg("Rate Date is invalid. Please enter a valid date.");
        return;
      }
    }

    const newFParams = {
      ...fParams,
      rate_date: fParams.rate_date
        ? moment(fParams.rate_date).format("YYYY-MM")
        : null,
    };

    if (newFParams.to_currency === "all") {
      delete newFParams.to_currency;
    }

    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Click button [Apply Filter] - Search Exchange Rates list", fParams);

    setFilter(newFParams);
    queryPage(newPagination, newFParams);
  };

  const schema = [
    {
      type: "select",
      label: "To Currency",
      param: "to_currency",
      options: [
        { name: "All", id: "all" },
        ...(exchangeRateFilter?.to_currency?.map((currency) => ({
          id: currency,
          name: currency,
        })) || []),
      ],
    },
    {
      type: "month",
      label: "Rate Date",
      param: "rate_date",
    },
  ];

  const handleCreate = () => {
    Log.info("[Info] - Click button [Add new exchange rate] - Open dialog to Create A New Exchange Rate");
    setOpenCreateDialog(true);
  };

  const handleEdit = (id) => {
    Log.info(`[Info] - Click button [Edit] - Open dialog to Edit Exchange Rate Id: ${id}`);
    setSelectedId(id);
    setOpenEditDialog(true);
  };

  const handleReset = () => {
    setDefaultValue({});
    setFormKey(prev => prev + 1);
  };

  return (
    <Box>
      {exchangeRateRole.includes("Add") && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<Add />}
            sx={{ ...styles.customButton }}
          >
            Add New Exchange Rate
          </Button>
        </Box>
      )}
      <FormSearch
        key={formKey}
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
        onReset={handleReset}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map((row) => {
                    const {
                      id,
                      to_currency,
                      rate_date,
                      rate,
                    } = row;

                    const rateDateMoment = rate_date ? moment(rate_date) : null;
                    const isBefore2025 = rateDateMoment && rateDateMoment.year() < EXCHANGE_RATE_YEAR_THRESHOLD;

                    return (
                      <StyledTableRow hover key={id}>
                        <TableCell>{HARDCODED_FROM_CURRENCY}</TableCell>
                        <TableCell>{to_currency || ""}</TableCell>
                        <TableCell>
                          {rate_date
                            ? moment(rate_date).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>
                          {rate !== null && rate !== undefined
                            ? parseFloat(rate).toFixed(7)
                            : "0.0000000"}
                        </TableCell>
                        <TableCell align="right">
                          {!isBefore2025 && exchangeRateRole.includes("Edit") && (
                            <Tooltip title="Edit" placement="top">
                              <IconButton
                                onClick={() => handleEdit(id)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
      {openCreateDialog && (
        <EditDialog
          open={true}
          onClose={handleCloseDialog}
          onSuccess={() => {
            handleCloseDialog();
            queryPage(pagination, filter);
          }}
        />
      )}
      {openEditDialog && selectedId && (
        <EditDialog
          key={selectedId}
          open={true}
          onClose={handleCloseDialog}
          selectedId={selectedId}
          onSuccess={() => {
            handleCloseDialog();
            queryPage(pagination, filter);
          }}
        />
      )}
    </Box>
  );
};

export default ExchangeRates;
